import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SajuInput from './components/SajuInput';
import SajuDisplay from './components/SajuDisplay';
import CoinToss from './components/CoinToss';
import HexagramResult from './components/HexagramResult';
import InterpretationView from './components/InterpretationView';
import FeedbackWidget from './components/FeedbackWidget';
import apiService from '../../services/apiService';

const Container = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  color: #e5e5e5;
  padding: 20px;
  font-family: 'Inter', sans-serif;
  max-width: 680px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 6px;
  &:hover { color: #e5e5e5; background: #1a1a1a; }
`;

const PageTitle = styled.div``;

const TitleMain = styled.h1`
  font-size: 22px;
  font-weight: 900;
  background: linear-gradient(135deg, #a78bfa, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const TitleSub = styled.p`
  color: #6b7280;
  font-size: 13px;
  margin: 2px 0 0 0;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
`;

const StepTitle = styled.h3`
  color: #a78bfa;
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 12px 0;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  background: #111;
  border: 1px solid #333;
  border-radius: 8px;
  color: #e5e5e5;
  font-size: 14px;
  line-height: 1.6;
  padding: 12px;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #8b5cf6;
  }
  &::placeholder { color: #4b5563; }
`;

const ExampleList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const ExampleChip = styled.button`
  background: #111;
  border: 1px solid #333;
  border-radius: 20px;
  color: #9ca3af;
  font-size: 12px;
  padding: 5px 12px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: #8b5cf6;
    color: #c4b5fd;
  }
`;

const EXAMPLE_QUESTIONS = [
  '오늘의 운세는?',
  '이 결정을 내려도 될까?',
  '현재 하는 일이 잘 풀릴까?',
  '새로운 시작을 해도 될까?',
  '지금 이 관계는 어떻게 될까?',
  '올해 재물운은?',
];

const ProceedBtn = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 13px;
  background: linear-gradient(135deg, #7c3aed, #5b21b6);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

export default function IchingPage({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sajuText, setSajuText] = useState('');
  const [sajuStructured, setSajuStructured] = useState(null);
  const [question, setQuestion] = useState('');
  const [localHex, setLocalHex] = useState(null);
  const [localInterpretation, setLocalInterpretation] = useState('');
  const [localSessionId, setLocalSessionId] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  const activeHex = localHex;

  const handleCoinComplete = (data) => {
    setLocalHex(data);
    setStep(2);
  };

  const handleInterpret = async () => {
    if (!activeHex || !question.trim()) return;
    setStep(3);
    await interpretWithData(activeHex);
  };

  const interpretWithData = async (hexData) => {
    setLocalLoading(true);
    setLocalError(null);
    const { gua, changedGua, changingLines, activeLine } = hexData;
    const guaPayload = {
      id: gua.id,
      name: gua.name.ko,
      cn: gua.name.cn,
      binary: gua.binary,
      judgment: gua.judgment,
      image: gua.image,
      changingLines,
      activeLine,
      changedGuaName: changedGua ? changedGua.name.ko : '',
    };
    try {
      const res = await apiService.interpretIching({
        sajuText,
        sajuStructured: sajuStructured || null,
        question,
        gua: guaPayload,
        userId: user?.email || 'anonymous',
      });
      setLocalInterpretation(res.interpretation);
      setLocalSessionId(res.sessionId);
      setStep(4);
    } catch (e) {
      setLocalError('해석에 실패했습니다. 다시 시도해 주세요.');
      setStep(3);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleFeedback = async (score, sajuHelped) => {
    if (!localSessionId) return;
    await apiService.submitIchingFeedback(localSessionId, score, sajuHelped);
  };

  const handleReset = () => {
    setLocalHex(null);
    setLocalInterpretation('');
    setLocalSessionId(null);
    setLocalError(null);
    setStep(0);
    setQuestion('');
    setSajuText('');
    setSajuStructured(null);
  };

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate('/labs')}>
          <ArrowLeft size={20} />
        </BackBtn>
        <PageTitle>
          <TitleMain>☯ 주역 × 사주 × AI</TitleMain>
          <TitleSub>동전점으로 괘를 뽑고 AI가 해석합니다</TitleSub>
        </PageTitle>
      </Header>

      <Stack>
        {/* Step 1 — 사주 입력 */}
        <SajuInput
          onSajuChange={setSajuText}
          onSajuStructuredChange={setSajuStructured}
        />
        {sajuStructured && <SajuDisplay sajuStructured={sajuStructured} />}

        {/* Step 2 — 질문 입력 */}
        <Card>
          <StepTitle>Step 2 — 질문 입력</StepTitle>
          {!question && (
            <ExampleList>
              {EXAMPLE_QUESTIONS.map(q => (
                <ExampleChip key={q} onClick={() => setQuestion(q)}>{q}</ExampleChip>
              ))}
            </ExampleList>
          )}
          <Textarea
            placeholder="지금 마음에 걸리는 것, 결정해야 할 일, 알고 싶은 것을 적어주세요."
            value={question}
            onChange={e => setQuestion(e.target.value)}
          />
        </Card>

        {/* Step 3 — 동전 던지기 */}
        {step < 2 && (
          <ProceedBtn
            disabled={!question.trim()}
            onClick={() => setStep(1)}
          >
            동전 던지기 시작 →
          </ProceedBtn>
        )}

        {step >= 1 && (
          <CoinToss onComplete={handleCoinComplete} />
        )}

        {/* Step 4 — 괘 결과 */}
        {activeHex && (
          <HexagramResult
            gua={activeHex.gua}
            changedGua={activeHex.changedGua}
            changingLines={activeHex.changingLines}
            activeLine={activeHex.activeLine}
          />
        )}

        {/* AI 해석 버튼 */}
        {activeHex && step < 3 && !localInterpretation && (
          <ProceedBtn
            disabled={localLoading}
            onClick={handleInterpret}
          >
            AI 해석 받기 →
          </ProceedBtn>
        )}

        {/* Step 5 — AI 해석 */}
        {(localLoading || localInterpretation || localError) && (
          <InterpretationView
            isLoading={localLoading}
            text={localInterpretation}
            error={localError}
            onRetry={handleInterpret}
          />
        )}

        {/* Step 6 — 피드백 */}
        {localInterpretation && !localLoading && (
          <FeedbackWidget
            onFeedback={handleFeedback}
            onReset={handleReset}
            hasSaju={!!sajuStructured}
          />
        )}
      </Stack>
    </Container>
  );
}
